/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';
import {WINDOW} from '../../app/angular-platform-providers';

const DEFAULT_HOME_STATE = 'app.home';
const DEFAULT_NOT_FOUND_STATE = 'app.errors.404';
const DEFAULT_SIGN_IN_STATE = 'sign-in';

function stateName(stateOrName: any): string | undefined {
  return _.isString(stateOrName) ? stateOrName : _.get(stateOrName, 'name');
}

function isSignInDestination(dest: string): boolean {
  return !dest || dest === '/sign-in' || _.startsWith(dest, '/sign-in/') || _.startsWith(dest, '/sign-in?');
}

function getInternalDestination(window: any, dest: any): string | null {
  if (!_.isString(dest) || !dest) {
    return null;
  }
  try {
    const currentUrl = new URL(window.location.href || window.location.origin);
    const destination = new URL(dest, currentUrl);
    if (destination.origin !== currentUrl.origin) {
      return null;
    }
    return destination.pathname + destination.search + destination.hash;
  } catch (_error) {
    return null;
  }
}

function fallbackNavigation($state: any, homeState: string, notFoundState: string): any {
  return $state.go(notFoundState, null, {inherit: false, location: false})
    .catch(function() {
      return $state.go(homeState, null, {inherit: false});
    });
}

function stateHref($state: any, state: string, params: any): string | null {
  return $state.href(state, params, {inherit: false}) || null;
}

function hardNavigateFromSignIn($state: any, window: any, signInState: string, url: string, onSuccess?: () => void): any {
  if ($state.includes(signInState) && url) {
    window.location.assign(url);
    return Promise.resolve().then(onSuccess);
  }
  return null;
}

@Injectable({
  providedIn: 'root'
})
export class AdnLoginNavigationService {
  constructor(
    @Inject(WINDOW) private readonly window: any
  ) {
  }

  navigateAfterLogin($state: any, location: any, $urlRouter: any, options: any): any {
    const dest = getInternalDestination(this.window, options.dest);
    const signInState = options.signInState || DEFAULT_SIGN_IN_STATE;
    const homeState = options.homeState || DEFAULT_HOME_STATE;
    const notFoundState = options.notFoundState || DEFAULT_NOT_FOUND_STATE;
    const onSuccess = options.onSuccess || (() => undefined);
    const fallback = () => fallbackNavigation($state, homeState, notFoundState);

    if (dest && !isSignInDestination(dest)) {
      const hardNav = hardNavigateFromSignIn($state, this.window, signInState, dest, onSuccess);
      if (hardNav) {
        return hardNav;
      }
      location.navigate(dest);
      $urlRouter.sync();
      return Promise.resolve().then(() => onSuccess());
    }

    const hardNav = hardNavigateFromSignIn(
      $state,
      this.window,
      signInState,
      stateHref($state, homeState, null) || '/home',
      onSuccess
    );
    if (hardNav) {
      return hardNav;
    }

    return $state.go(homeState, null, {inherit: false}).then(onSuccess, function() {
      return fallback().then(onSuccess);
    });
  }

  navigateToStateOrHome($state: any, toState: any, toParams: any, options?: any): any {
    const homeState = (options && options.homeState) || DEFAULT_HOME_STATE;
    const signInState = (options && options.signInState) || DEFAULT_SIGN_IN_STATE;
    const state = stateName(toState);
    const targetState = state || homeState;
    const targetParams = state ? toParams : null;
    const hardNav = hardNavigateFromSignIn(
      $state,
      this.window,
      signInState,
      stateHref($state, targetState, targetParams)
    );
    if (hardNav) {
      return hardNav;
    }
    if (!state) {
      return $state.go(homeState, null, {inherit: false});
    }
    return $state.go(state, toParams, {inherit: false}).catch(function() {
      return $state.go(homeState, null, {inherit: false});
    });
  }
}
